import { useQueryClient, type Query } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { queryKeys } from '@/constant/common'
import { removeStation } from '@/store/slices/MapView/mapViewSlice'
import { publishStationDeletion } from '@/store/slices/MapView/stationDeletionEvent'

const RELATED_QUERY_FAMILIES = new Set([
  queryKeys.stations,
  queryKeys.station,
  queryKeys.stationsProgram,
  queryKeys.program,
  queryKeys.etZone,
  queryKeys.controller,
  queryKeys.guidedAllDecoders,
  queryKeys.setupCommonData,
  queryKeys.irrigationOverview,
])

const IRRIGATION_QUERY_FAMILIES = new Set([
  'availableStations',
  'irrigation-overview-raw',
  'irrigation-overview-data',
  'irrigation-overview',
  'stationAssignments',
  'upcomingPrograms',
])

const isRelatedQuery = (query: Query, interfaceId: number): boolean => {
  const [first, controller] = query.queryKey
  if (first === queryKeys.stations && controller === 'delete-safety') return false
  const family = Array.isArray(first) ? first[0] : first
  if (typeof family !== 'string') return false
  if (IRRIGATION_QUERY_FAMILIES.has(family)) return Number(controller) === interfaceId
  return RELATED_QUERY_FAMILIES.has(family)
}

export const useStationDeletionRefresh = (): (
  interfaceId: number,
  stationId: number,
) => Promise<void> => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()

  return async (interfaceId, stationId) => {
    dispatch(removeStation({ interfaceId, stationId }))
    publishStationDeletion({ interfaceId, stationId })
    const predicate = (query: Query): boolean => isRelatedQuery(query, interfaceId)
    try {
      const safetyKey = [queryKeys.stations, 'delete-safety', interfaceId, stationId]
      await queryClient.cancelQueries({ queryKey: safetyKey, exact: true }, { silent: true })
      queryClient.removeQueries(safetyKey, { exact: true })
      await queryClient.cancelQueries({ predicate })
      await queryClient.invalidateQueries({ predicate, refetchType: 'none' })
      await queryClient.refetchQueries({ predicate, type: 'active' }, { throwOnError: true })
      await queryClient.invalidateQueries(['processed-station-assignments', interfaceId])
    } catch (error) {
      console.error('Station deleted, but refreshing related data failed:', error)
    }
  }
}
